export default function SunnyIslesDestinationsSection() {
  const stats = [
    {
      value: "60%",
      label: "of Sunny Isles Beach requests are Russia/Eastern Europe, Israel, or Latin America with under 3 weeks notice",
    },
    {
      value: "2 airports",
      label: "within 35 minutes — FLL and MIA — giving Sunny Isles Beach unmatched departure flexibility",
    },
    {
      value: "Nov–Jan",
      label: "peak travel season — holiday family visits, bar/bat mitzvah trips, and snowbird arrivals converge",
    },
    {
      value: "24-hour",
      label: "shortest emergency window we've accommodated for a Sunny Isles Beach traveler",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "Russia & Eastern Europe",
      context: "Moscow, St. Petersburg, Kiev, Minsk, and Tashkent are not vacation destinations for many Sunny Isles Beach residents — they are home. The city's remarkable Russian and Eastern European community maintains continuous family, business, and cultural ties to the region. Direct and connecting flights from MIA and FLL serve these routes, but Russia's strict six-month passport validity requirement and complex visa process mean that an expired passport is a genuine crisis.",
      urgency: "Family emergencies in Russia often require travel within 2–7 days — far too short for standard government processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Israel",
      context: "Jerusalem, Tel Aviv, Haifa, and the Dead Sea draw Sunny Isles Beach's Jewish community for bar and bat mitzvah celebrations, family visits, religious pilgrimages for Passover and Rosh Hashanah, and business connections in Israel's thriving tech and finance sectors. Israel requires six months of passport validity, and the emotional and religious significance of these trips means that missing one is a genuine family crisis, not a travel inconvenience.",
      urgency: "Bar and bat mitzvah trips are planned 6–12 months in advance, but passport discoveries often happen 2–4 weeks before departure",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "Caribbean cruises from Port Everglades and the Port of Miami are a staple for Sunny Isles Beach families, couples, and groups of friends. The Bahamas, Jamaica, the Cayman Islands, and the Eastern Caribbean are all accessible on three- to seven-day itineraries. Sunny Isles Beach's proximity to both cruise ports makes spontaneous bookings common — which creates last-minute passport urgency when a family member's document is discovered expired.",
      urgency: "Cruise bookings can happen 1–3 weeks before sailing — the shortest notice window of any Sunny Isles Beach travel pattern",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Colombia & Venezuela",
      context: "Bogota, Medellin, Caracas, and Maracaibo are common destinations for Sunny Isles Beach's Colombian and Venezuelan residents, who maintain deep family, business, and cultural ties to their home countries. Many families travel two to four times per year for weddings, funerals, birthdays, and business meetings. Direct flights from MIA make these trips routine, but passport validity requirements are strict — six months is standard.",
      urgency: "Family visit bookings often happen 2–4 weeks before travel — too short for standard processing if a passport has expired",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Brazil & Argentina",
      context: "Sao Paulo, Rio de Janeiro, and Buenos Aires draw Sunny Isles Beach's Brazilian and Argentine residents for family visits, Carnival, and summer holidays (Brazil's summer is December–February, aligning with South Florida's snowbird season). Business travelers fly to Sao Paulo for finance, tech, and manufacturing meetings. Both countries require six months of passport validity, and Brazil's visa reciprocity rules for U.S. visitors can be complex.",
      urgency: "Carnival and holiday bookings spike in December — passport discoveries spike in January when travelers realize documents expired",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe",
      context: "Paris, London, Rome, Barcelona, Saint-Tropez, and the Greek Islands draw Sunny Isles Beach's affluent residents for luxury vacations, art events, fashion weeks, and extended summer tours. The city's luxury lifestyle and international outlook mean that European travel is not an occasional splurge — it is a regular part of the annual calendar. The Schengen Area requires three months of passport validity beyond your planned departure, and many airlines enforce six months at check-in.",
      urgency: "European summer bookings peak in March–April — passport renewals should be completed by May at the latest",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Sunny Isles Beach Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Sunny Isles Beach
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Sunny Isles Beach travelers head to Russia, Israel, Latin America, the Caribbean, and Europe for a mix of family visits, business, religious pilgrimages, and luxury vacations. These are the destinations that create the most passport urgency for our Sunny Isles Beach clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Miami", href: "/cities/miami/" },
              { name: "Fort Lauderdale", href: "/cities/fort-lauderdale/" },
              { name: "Aventura", href: "/cities/aventura/" },
              { name: "Boca Raton", href: "/cities/boca-raton/" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach/" },
              { name: "Hollywood", href: "/cities/hollywood/" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}